/**
 * CSP Polyfill for Angular
 * This file patches various browser APIs to add nonce attributes to dynamically created styles and scripts
 */

declare global {
  interface Window {
    CSP_NONCE: string;
    SCRIPT_NONCE: string;
  }
}

export function initCSPPolyfill() {
  // Set default nonces
  window.CSP_NONCE = 'automatrdynamicstyle';
  window.SCRIPT_NONCE = 'automatrdynamicscripts';

  // Patch Angular's style injection for components
  patchAngularStyles();
  
  // Patch third-party libraries (especially PrimeNG)
  patchThirdPartyLibraries();
  
  // Additional patch for PrimeNG 19 compatibility
  patchPrimeNG();
}

function patchAngularStyles() {
  // Override appendChild to add nonce to style elements
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function<T extends Node>(newChild: T): T {
    if (newChild instanceof HTMLStyleElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', window.CSP_NONCE);
    } else if (newChild instanceof HTMLScriptElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', window.SCRIPT_NONCE);
    }
    return originalAppendChild.call(this, newChild);
  };

  // Override insertBefore to add nonce to style elements
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function<T extends Node>(newChild: T, refChild: Node | null): T {
    if (newChild instanceof HTMLStyleElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', window.CSP_NONCE);
    } else if (newChild instanceof HTMLScriptElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', window.SCRIPT_NONCE);
    }
    return originalInsertBefore.call(this, newChild, refChild);
  };

  // Store original setAttribute before any modifications
  const originalSetAttribute = Element.prototype.setAttribute;
  
  // Override setAttribute to handle inline styles that violate CSP
  Element.prototype.setAttribute = function(name: string, value: string) {
    // Special case: allow nonce and id attributes to be set normally
    if (name === 'nonce' || name === 'id' || name === 'class') {
      return originalSetAttribute.call(this, name, value);
    }
    
    if (name === 'style') {
      // For ANY element (not just HTMLElement), prevent inline style setting
      // This catches PrimeNG components that might use different element types
      
      if (!this || typeof this !== 'object') {
        console.warn('Invalid element for style setting');
        return;
      }
      
      // Try using style property instead of creating inline styles
      try {
        if (value && typeof value === 'string' && (this as any).style) {
          const styleObj = (this as any).style;
          
          // Don't clear existing styles, merge them
          if (value.includes(':')) {
            const rules = value.split(';').filter(rule => rule.trim());
            rules.forEach(rule => {
              const colonIndex = rule.indexOf(':');
              if (colonIndex > 0) {
                const property = rule.substring(0, colonIndex).trim();
                const val = rule.substring(colonIndex + 1).trim();
                if (property && val) {
                  try {
                    // Use setProperty for better compatibility
                    if (styleObj.setProperty) {
                      styleObj.setProperty(property, val);
                    } else {
                      // Fallback to direct property access
                      const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                      styleObj[camelProperty] = val;
                    }
                  } catch (propError) {
                    console.warn(`Failed to set style property ${property}:`, propError);
                  }
                }
              }
            });
          }
        }
      } catch (e) {
        console.warn('Failed to apply styles via style property, skipping to avoid CSP violation:', e);
      }
      return; // Never call original setAttribute for style to avoid CSP violation
    }
    
    return originalSetAttribute.call(this, name, value);
  };

  // Patch innerHTML to wrap content in nonce-protected elements
  const originalInnerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  if (originalInnerHTML) {
    Object.defineProperty(Element.prototype, 'innerHTML', {
      set: function(value: string) {
        // Check if setting innerHTML with style tags
        if (typeof value === 'string' && value.includes('<style')) {
          // Parse and add nonce to style tags
          const temp = document.createElement('div');
          temp.innerHTML = value;
          const styles = temp.querySelectorAll('style');
          styles.forEach(style => {
            style.setAttribute('nonce', window.CSP_NONCE);
          });
          value = temp.innerHTML;
        }
        if (originalInnerHTML.set) {
          originalInnerHTML.set.call(this, value);
        }
      },
      get: function() {
        return originalInnerHTML.get ? originalInnerHTML.get.call(this) : '';
      }
    });
  }
}

function patchThirdPartyLibraries() {
  // Patch for PrimeNG and other libraries that create styles dynamically
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLStyleElement && !node.hasAttribute('nonce')) {
          node.setAttribute('nonce', window.CSP_NONCE);
        } else if (node instanceof HTMLScriptElement && !node.hasAttribute('nonce')) {
          node.setAttribute('nonce', window.SCRIPT_NONCE);
        }
      });
    });
  });

  // Start observing when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.head, { childList: true, subtree: true });
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    observer.observe(document.head, { childList: true, subtree: true });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

function patchPrimeNG() {
  // Override style property setter to intercept PrimeNG's direct style manipulation
  const originalStyleDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'style');
  if (originalStyleDescriptor) {
    Object.defineProperty(HTMLElement.prototype, 'style', {
      get: function() {
        return originalStyleDescriptor.get ? originalStyleDescriptor.get.call(this) : null;
      },
      set: function(value) {
        if (typeof value === 'string' && value) {
          // Convert string styles to individual property assignments
          try {
            const currentStyle = this.getAttribute('style') || '';
            // Use the style object's cssText property instead
            if (originalStyleDescriptor.get) {
              const styleObj = originalStyleDescriptor.get.call(this);
              if (styleObj) {
                styleObj.cssText = value;
                return;
              }
            }
          } catch (e) {
            console.warn('Failed to apply PrimeNG styles:', e);
          }
        } else if (originalStyleDescriptor.set) {
          originalStyleDescriptor.set.call(this, value);
        }
      }
    });
  }

  // Specific patch for PrimeNG DomHandler utility
  if (typeof window !== 'undefined') {
    // Wait for PrimeNG to load
    setTimeout(() => {
      // Check if PrimeNG's DomHandler exists
      if ((window as any).primeng && (window as any).primeng.dom && (window as any).primeng.dom.DomHandler) {
        const DomHandler = (window as any).primeng.dom.DomHandler;
        
        // Override addClass and removeClass methods that might set inline styles
        const originalAddClass = DomHandler.addClass;
        if (originalAddClass) {
          DomHandler.addClass = function(element: any, className: string) {
            if (element && className && !className.includes('style=')) {
              return originalAddClass.call(this, element, className);
            }
          };
        }
      }
    }, 0);
  }
}

// Initialize the polyfill
initCSPPolyfill();